import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { CalculateMedicalServiceMaximumUsageResponse } from "capitalrx-types";

import { AuthenticationContext } from "../context";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { MemberEligibilityInfo } from "../types";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";

export interface MemberAccumulationsBalances {
  readonly start_date: string;
  readonly end_date: string;
  readonly individual_amount: string;
  readonly family_amount: string;
  readonly individual_max_amount: string;
  readonly family_max_amount: string;
}

export interface MemberAccumulationsResponse {
  readonly deductible: MemberAccumulationsBalances;
  readonly category_deductible_1: MemberAccumulationsBalances;
  readonly out_of_pocket: MemberAccumulationsBalances;
  readonly out_of_pocket_category_1: MemberAccumulationsBalances;
  readonly plan_pay_fertility: MemberAccumulationsBalances;
  readonly plan_pay: MemberAccumulationsBalances;
  readonly gross_covered_drug_cost: MemberAccumulationsBalances;
  readonly true_out_of_pocket: MemberAccumulationsBalances;
  readonly part_b_maximum_out_of_pocket: MemberAccumulationsBalances;
  readonly part_b_deductible: MemberAccumulationsBalances;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly custom_accumulations: any;
}

export interface MemberMedicarePrescriptionPaymentPlanAccumulationBalancesResponse {
  readonly accumulations: string;
}

export interface UnifiedAccumulationItem {
  readonly end_date: string;
  readonly family_accumulated: string;
  readonly family_maximum: string;
  readonly individual_accumulated: string;
  readonly individual_maximum: string;
  readonly network: string;
  readonly pharmacy_accumulator: string;
  readonly start_date: string;
  readonly type: string;
}

export interface SeparableAccumulationItem extends UnifiedAccumulationItem {
  readonly applies_to: string[];
  readonly is_embedded: boolean | undefined;
}

export interface UnifiedAccumulationResponse {
  readonly [key: string]: UnifiedAccumulationItem;
}

export interface SeparableAccumulationsResponse {
  readonly results: Array<SeparableAccumulationItem>;
}

export const listMemberUnifiedAccumulations = async (authToken: string, body?: ViewRequest) => {
  return request<ViewRequest | {}, UnifiedAccumulationResponse>(
    {
      endpoint: "/member/unified_accumulation",
      body: { ...(body ?? {}) },
    },
    authToken,
  );
};

export const listMemberSeparableAccumulations = async (authToken: string, body?: ViewRequest) => {
  return request<ViewRequest | {}, SeparableAccumulationsResponse>(
    {
      endpoint: "/member/separable_accumulation",
      body: { ...(body ?? {}) },
    },
    authToken,
  );
};

export const useListMemberUnifiedAccumulationsQuery = (enabled: boolean) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const queryKey = ["memberUnifiedAccumulations", ...getEligibilityBasedQueryKey(memberEligibility)];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMemberUnifiedAccumulations(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return listMemberUnifiedAccumulations(await getAuthToken());
      }
    },
    queryKey: queryKey,
    enabled: enabled,
  });

  return query;
};

export const useListMemberSeparableAccumulationsQuery = (enabled: boolean) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const queryKey = ["memberSeparableAccumulations", ...getEligibilityBasedQueryKey(memberEligibility)];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMemberSeparableAccumulations(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return listMemberSeparableAccumulations(await getAuthToken());
      }
    },
    queryKey: queryKey,
    enabled: enabled,
  });

  return query;
};

export const listMemberAccumulations = async (authToken: string, body?: ViewRequest) => {
  return request<ViewRequest | {}, MemberAccumulationsResponse>(
    {
      endpoint: "/member/accumulation",
      body: { ...(body || {}) },
    },
    authToken,
  );
};

export const useListMemberAccumulationsQuery = (enabled: boolean) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const queryKey = ["memberAccumulations", ...getEligibilityBasedQueryKey(memberEligibility)];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMemberAccumulations(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return listMemberAccumulations(await getAuthToken());
      }
    },
    queryKey: queryKey,
    enabled: enabled,
  });

  return query;
};

export const calculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances = async (
  authToken: string,
  body?: Partial<ViewRequest> & { plan_year: number },
) => {
  return request<Partial<ViewRequest> & { plan_year: number }, MemberMedicarePrescriptionPaymentPlanAccumulationBalancesResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/accumulation_balances",
      body: { ...body },
    },
    authToken,
  );
};

export const useCalculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances = (enabled: boolean, planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const queryKey = ["memberMedicarePrescriptionPaymentPlanAccumulationBalances", planYear, ...getEligibilityBasedQueryKey(memberEligibility)];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return calculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return calculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances(await getAuthToken(), { plan_year: planYear } as ViewRequest & { plan_year: number });
      }
    },
    queryKey: queryKey,
    enabled: enabled,
  });

  return query;
};

export interface ListMemberMedicalServiceMaximumsRequest {
  readonly person_code?: string;
}

export const listMemberMedicalServiceMaximums = (authToken: string, body?: ListMemberMedicalServiceMaximumsRequest | ViewRequest) => {
  return request<ListMemberMedicalServiceMaximumsRequest | ViewRequest | {}, CalculateMedicalServiceMaximumUsageResponse> (
    {
      endpoint: "member/calculate_medical_service_maximum_usage",
      body: { ...body || {} },
    },
    authToken,
  );
};

export const useListMemberMedicalServiceMaximumsQuery = (body: ListMemberMedicalServiceMaximumsRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const queryKey = ["listMemberMedicalServiceLimits", ...getEligibilityBasedQueryKey(memberEligibility)];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async ( ) => {
      if(isViewingDataholder){
        return listMemberMedicalServiceMaximums(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return listMemberMedicalServiceMaximums(await getAuthToken(), { ...body });
      }
    },
    queryKey: queryKey,
  });

  return query;
};

export interface MedicareAmount {
  readonly id: number;
  readonly effective_date: string;
  readonly deductible_phase_maximum: number;
  readonly initial_coverage_phase_coinsurance_percent: number;
  readonly gross_covered_drug_cost_maximum: number;
  readonly coverage_gap_phase_coinsurance_percent: number;
  readonly coverage_gap_gap_discount_amount_percent: number;
  readonly true_out_of_pocket_maximum: number;
  readonly catastrophic_phase_coinsurance_percent: number;
  readonly catastrophic_phase_generic_copay_amount: number;
  readonly catastrophic_phase_brand_copay_amount: number;
  readonly part_b_deductible_phase_maximum: number;
  readonly senior_savings_model_cost_share_maximum_amount: number;
  readonly out_of_pocket_threshold: number;
}

export interface MedicareAmountGetRequest {
  readonly year: number;
}

export const getMemberMedicareAmount = async (authToken: string, body: MedicareAmountGetRequest) => {
  return request<MedicareAmountGetRequest, MedicareAmount>(
    {
      endpoint: "/medicare_amount/get",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetMemberMedicareAmountQuery = (filters: MedicareAmountGetRequest) => {
  const queryKey = ["medicareAmountGet", filters.year];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      return getMemberMedicareAmount(await getAuthToken(), { ...filters });
    },
    queryKey: queryKey,
  });

  return query;
};

export interface ListMedicareAmountResponse {
  readonly results: {
    readonly medicare_amount: MedicareAmount;
  }[];
  readonly result_count: string;
}

export const listMemberMedicareAmounts = async (authToken: string) => {
  return request<never, ListMedicareAmountResponse>(
    {
      endpoint: "/medicare_amount/list",
      body: {} as never,
    },
    authToken,
  );
};

export const useListMemberMedicareAmountQuery = () => {
  const queryKey = ["medicareAmountList"];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      return listMemberMedicareAmounts(await getAuthToken());
    },
    queryKey: queryKey,
  });

  return query;
};

export interface MemberClaimListRequest {
  readonly id?: number;
  readonly client_id?: string;
  readonly external_member_id?: string;
  readonly person_code?: string;
  readonly submitted_cardholder_id?: string;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly claim_status?: string;
  readonly date_type?: string;
  readonly prescription_service_reference_number?: string;
  readonly prescriber_id?: string;
  readonly ndc?: string;
  readonly service_provider_id?: string;
  readonly start_date?: string;
  readonly end_date?: string;
  readonly reject_codes?: string;
  readonly paid_claims_only?: boolean;
  readonly claim_source?: string[];
  readonly page_number?: number;
  readonly results_per_page?: number;
  readonly full_text_search?: string;
  readonly get_dependent_claims?: boolean;
  readonly month?: number;
  readonly year?: number;
  readonly year_to_date?: boolean;
  readonly coverage_strategy_type?: string;
}

export interface MemberClaim {
  readonly amount_of_copay?: string;
  readonly amount_of_coinsurance?: string;
  readonly drug_id?: string;
  readonly dosage_form_packaging?: string;
  readonly drug_type?: "Brand" | "Generic";
  readonly drug_name?: string;
  readonly dosage?: string;
  readonly pharmacy_nabp?: string;
  readonly brand_name_code: string;
  readonly cardholder_first_name: string;
  readonly cardholder_last_name: string;
  readonly date_of_service: string;
  readonly date_prescription_written: string;
  readonly days_supply: number;
  readonly direct_member_reimbursement_indicator?: string | null;
  readonly dosage_form: string;
  readonly external_member_id: string;
  readonly fill_number: number;
  readonly id?: number;
  readonly ndc: string;
  readonly patient_first_name: string;
  readonly patient_last_name: string;
  readonly patient_pay_amount?: string;
  readonly plan_pay_amount?: string;
  readonly pharmacy_name: string;
  readonly prescriber_first_name: string;
  readonly prescriber_last_name: string;
  readonly product_service_description: string;
  readonly quantity_dispensed: string;
  readonly service_provider_id: string;
  readonly strength: string;
  readonly strength_unit_of_measure: string;
  readonly total_amount_paid?: string;
  readonly unit_of_measure: string;
  readonly savings_opportunity?: boolean;
  readonly relationship_code?: string;
  readonly use_customer_support_hotline?: boolean;
  readonly coverage_strategy_type?: string;
}

export interface MemberClaimListResponse {
  readonly results: { readonly claim: MemberClaim }[];
  readonly result_count: string;
}

export const listMemberClaims = async (authToken: string, body?: Partial<ViewRequest> & MemberClaimListRequest) => {
  return request<typeof body, MemberClaimListResponse>(
    {
      endpoint: "/member/claim/list",
      body: { ...(body || {}) },
    },
    authToken,
  );
};

export const useListMemberClaimsQuery = (body: MemberClaimListRequest, enabled?: boolean) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMemberClaims(await getAuthToken(), {
          ...body,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listMemberClaims(await getAuthToken(), { ...body });
      }
    },
    queryKey: [
      "memberClaimsList",
      body?.page_number || 1,
      body?.coverage_strategy_type || "",
      ...getEligibilityBasedQueryKey(memberEligibility),
    ],
    enabled: enabled,
  });

  return query;
};

export const getMemberClaim = async (authToken: string, body: { readonly id: number } & Partial<ViewRequest>) => {
  const response = await request<typeof body, MemberClaimListResponse>(
    {
      endpoint: "/member/claim/list",
      body: { ...body },
    },
    authToken,
  );
  return response?.results[0]?.claim;
};

export const useGetMemberClaim = (claimId: number) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getMemberClaim(await getAuthToken(), {
          ...{ id: claimId },
          ...getViewRequest(memberEligibility),
        });
      } else {
        return getMemberClaim(await getAuthToken(), { id: claimId });
      }
    },
    queryKey: ["claimGet", claimId, ...getEligibilityBasedQueryKey(memberEligibility)],
  });

  return query;
};

export interface MemberDataholderListResponse {
  readonly results: MemberEligibilityInfo[];
  readonly results_count: string;
}

export const listMemberDataholders = async (authToken: string) => {
  return request<{}, MemberDataholderListResponse>(
    {
      endpoint: "/member/dataholder_list",
      body: {},
    },
    authToken,
  );
};

export interface MemberDependent {
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  birth_date: string;
  city: string;
  country: string;
  coverage_status: string;
  coverage_type: string;
  created_at: string;
  death_date: string;
  effective_date: string;
  email: string;
  end_date: string;
  external_member_id: string;
  first_name: string;
  group_id: number;
  handicap_indicator: string;
  id: number;
  language_code: string;
  last_name: string;
  multiple_birth_code: string;
  person_code: string;
  phone_number: string;
  plan_id: string;
  ppo_name: string;
  relationship_code: string;
  sex: string;
  start_date: string;
  state: string;
  termination_date: string;
  updated_at: string;
  zip_code: string;
  is_minor_dependent: boolean;
}

export interface MemberDependentListResponse {
  readonly results: { readonly member: MemberDependent }[];
  readonly result_count: string;
}

export const listMemberDependents = async (authToken: string) => {
  return request<{}, MemberDependentListResponse>(
    {
      endpoint: "/member/dependent_list",
      body: {},
    },
    authToken,
  );
};

export const useListMemberDependentsQuery = () => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      return listMemberDependents(await getAuthToken());
    },
    queryKey: ["dependentList", ...getEligibilityBasedQueryKey(memberEligibility)],
    enabled: !isViewingDataholder,
  });

  return {
    ...query,
    enabled: !isViewingDataholder,
  };
};

export interface UpdateNotificationSettingsRequest {
  readonly opt_in_to_push_notifications?: boolean;
  readonly selected_notification_authenticator_id?: number;
  readonly opt_in_to_emails?: boolean;
}

export const updateMemberNotificationSettings = async (authToken: string, body: UpdateNotificationSettingsRequest) => {
  return request<UpdateNotificationSettingsRequest, never>(
    {
      endpoint: "/member/notification_settings_update",
      body: { ...body },
    },
    authToken,
  );
};
